import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { APP_LANGUAGES, getStoredLanguage, resolveLanguage, type AppLanguage } from '../utils/languageRouting';
import { HYDRATING } from '../utils/hydrating';
import { fromHtmlLang } from '../utils/htmlLang';
import { maintenanceResources } from './maintenance';

// A hydrating page starts in the language the server rendered it in (written on <html lang> by the build);
// the stored preference only applies after mount (MainLayout). Plain SPA loads resolve URL, then storage.
const prerenderedLanguage = HYDRATING ? fromHtmlLang(document.documentElement.lang) : '';
const initialLanguage: AppLanguage = typeof window === 'undefined'
  ? 'fr'
  : (APP_LANGUAGES as readonly string[]).includes(prerenderedLanguage)
    ? (prerenderedLanguage as AppLanguage)
    : resolveLanguage(window.location.pathname, getStoredLanguage());

/**
 * Effective dictionaries (French fallbacks already merged, see scripts/vite-effective-locales.mjs): one chunk per
 * language, so a page downloads only its own language. Other languages are loaded when the visitor switches.
 */
const dictionaryLoaders: Record<AppLanguage, () => Promise<{ default: Record<string, unknown> }>> = {
  fr: () => import('virtual:i18n/fr'),
  en: () => import('virtual:i18n/en'),
  ar: () => import('virtual:i18n/ar'),
  darija: () => import('virtual:i18n/darija'),
  darija_ar: () => import('virtual:i18n/darija_ar'),
};

const maintenanceOnly = Object.fromEntries(
  APP_LANGUAGES.map((lang) => [lang, { maintenance: maintenanceResources[lang] }]),
);

/** Loads the dictionary of `lang` into i18next if it is not there yet. */
export const ensureLanguageLoaded = async (lang: AppLanguage): Promise<void> => {
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  const { default: dictionary } = await dictionaryLoaders[lang]();
  i18n.addResourceBundle(lang, 'translation', dictionary, true, true);
};

/** Switches language: loads its dictionary first, then changes language (never shows raw keys). */
export const changeLanguageLoaded = async (lang: AppLanguage): Promise<void> => {
  await ensureLanguageLoaded(lang);
  await i18n.changeLanguage(lang);
};

// Export Promise pour garantir initialisation avant render
export const i18nReady = dictionaryLoaders[initialLanguage]()
  .then(({ default: dictionary }) => i18n
    .use(initReactI18next)
    .init({
      resources: {
        ...maintenanceOnly,
        [initialLanguage]: { translation: dictionary, maintenance: maintenanceResources[initialLanguage] },
      },
      lng: initialLanguage,
      fallbackLng: 'fr',
      interpolation: {
        escapeValue: false,
      },
      react: { useSuspense: false },
      initImmediate: false,
    }));

export default i18n;
